import React, { Component } from 'react';
import Widget from '../common/Widget';
import {
  IconButton,
  InputAdornment,
  Card,
  Typography,
  List,
  ListItem,
  OutlinedInput,
  Skeleton,
  Box,
} from '@mui/material';
import PlaceCard from '../common/PlaceCard';
import PlaceMap from '../common/PlaceMap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { faClose } from '@fortawesome/pro-light-svg-icons';
import PlaceCategories from './PlaceCategories';
import { getData } from '../../DataAccessLayer';
import { placesAndHours, CAMPUS_OVERVIEW_EMBED_URL } from '../../DataAccessLayer/services';
import { toggleAllCategories } from './actions';
import { connect } from 'react-redux';
import _ from 'lodash';
import ApiErrorMessage from '../common/ApiErrorMessage';

class PlacesAndHours extends Component {
  GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  firstResultRef = React.createRef();
  noResultsRef = React.createRef();

  state = {
    isExpand: true,
    data: [],
    searchContent: '',
    searchResults: [],
    isLoading: false,
    isError: false,
    onSearch: false,

    selectedLocation: null,
  };

  componentDidMount() {
    this.loadPlacesAndHours();
  }

  //O(1) Fetches all places and hours
  loadPlacesAndHours = async () => {
    this.setState({ isLoading: true, isError: false });
    try {
      const data = await getData(placesAndHours, true);

      this.setState({ data });
    } catch (err) {
      this.setState({ isError: true });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleExpand = () => {
    this.setState({ isExpand: !this.state.isExpand });
  };

  /**
   * Θ(N*M) - Toggles the expanded state of all categories and sets places within those categories based on the `isExpanded` parameter.
   */

  toggleAllCategoriesState = (isExpanded) => {
    this.props.toggleAllCategories(
      this.state.data.reduce((newState, category) => {
        // Directly set the expanded state for the category
        newState[category.id] = isExpanded;

        // Directly set the expanded state for all places within the category to false
        category.places.forEach((place) => (newState[place.id] = false));

        return newState;
      }, {}),
    );
  };

  handleSelectLocation = (location) => {
    this.setState({ selectedLocation: location });
  };

  handleClearSearch = () => {
    this.setState({ searchResults: [], searchContent: '', onSearch: false });
  };

  handleSearch = () => {
    let searchResults = [];

    const searchContent = this.state.searchContent.trim();

    if (searchContent) {
      searchResults = this.state.data.reduce((acc, item) => {
        // Perform a case-insensitive search
        const matches = item.places.filter((place) =>
          place.title.toLowerCase().includes(searchContent.toLowerCase()),
        );

        // Concatenate any found matches to the accumulator
        return acc.concat(matches);
      }, []);
    }
    this.setState(
      {
        searchResults,
        onSearch: true,
      },
      () => {
        if (searchResults.length > 0) {
          this.firstResultRef.current?.focus();
        } else {
          this.noResultsRef.current?.focus();
        }
      },
    );
  };

  handleKeyDownSearch = (event) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  getMenu = () => {
    return [
      {
        id: 'placesAndHours_expandAll',
        title: 'Expand All Categories',
        onClick: () => this.toggleAllCategoriesState(true),
      },
      {
        id: 'placesAndHours_collapseAll',
        title: 'Collapse All Categories',
        onClick: () => this.toggleAllCategoriesState(false),
      },
      //TODO: enable in V1.1
      // {id: 'placesAndHours_hide', title: 'Hide'},
    ];
  };

  render() {
    const { isLoading, data, isError, selectedLocation } = this.state;

    return (
      <>
        <Widget
          data={{
            id: this.props.widget.id,
            title: this.props.title,
            isTitleStylized: this.props.isTitleStylized,
            menu: this.getMenu(),
            isExpand: this.props.widget.isExpand,
            isRequired: this.props.widget.isRequired,
          }}
          hasScrollY
          className="myOdu__placesAndHours"
          {...this.props}
        >
          <div id="myOdu__placesAndHours_wrapper" className="wrapper">
            {isLoading ? (
              <Skeleton
                id={this.props.widget.id + '__loadingSkeleton'}
                variant="rectangular"
                height={300}
              />
            ) : data.length === 0 && !isLoading ? (
              isError ? (
                <ApiErrorMessage
                  id={this.props.widget.id + '__apiErrorMessage'}
                  widgetName={this.props.title}
                  reload={this.loadPlacesAndHours}
                />
              ) : (
                <Typography
                  id={this.props.widget.id + '__notFoundMessage'}
                  component="p"
                  variant="small"
                >
                  No places and hours found.
                </Typography>
              )
            ) : (
              <>
                <div className="stationaryTop">
                  <Card
                    className="search"
                    elevation={0}
                    id={this.props.widget.id + '__card_search'}
                    role="search"
                    aria-label="Search places and hours"
                  >
                    <OutlinedInput
                      id={this.props.widget.id + '__input_search'}
                      value={this.state.searchContent}
                      onChange={(e) =>
                        this.setState({
                          searchContent: e.target.value,
                        })
                      }
                      fullWidth
                      size="small"
                      label="Search Places and Hours"
                      className="myOdu__hideFloatLabel myOdu__border"
                      placeholder="Search Places and Hours"
                      onKeyDown={this.handleKeyDownSearch}
                      endAdornment={
                        <InputAdornment
                          position="end"
                          id={this.props.widget.id + '__inputAdornment_search'}
                        >
                          {this.state.searchResults.length > 0 || this.state.onSearch ? (
                            <IconButton
                              id={this.props.widget.id + '__iconButton_search_clear'}
                              aria-label="Clear Search Places and Hours"
                              edge="end"
                              onClick={this.handleClearSearch}
                            >
                              <FontAwesomeIcon
                                icon={faClose}
                                size="xs"
                                id={this.props.widget.id + '__icon_search_clear'}
                              />
                            </IconButton>
                          ) : (
                            <IconButton
                              id={this.props.widget.id + '__iconButton_search'}
                              aria-label="Search Places and Hours"
                              edge="end"
                              onClick={this.handleSearch}
                            >
                              <FontAwesomeIcon
                                icon={faSearch}
                                size="xs"
                                id={this.props.widget.id + '__icon_search'}
                              />
                            </IconButton>
                          )}
                        </InputAdornment>
                      }
                    />
                  </Card>

                  {this.state.onSearch && (
                    <Box
                      className="searchResults"
                      id={this.props.widget.id + '__box_searchResults'}
                      aria-live="polite"
                    >
                      <Typography
                        component="h3"
                        variant="h6"
                        sx={{ mb: 1 }}
                        id={this.props.widget.id + '__header_searchResults'}
                      >
                        Search Results ({this.state.searchResults.length} found)
                      </Typography>

                      {!_.isEmpty(this.state.searchResults) ? (
                        this.state.searchResults.map((result) => (
                          <Box
                            sx={{ mb: 1 }}
                            key={this.props.widget.id + '__searchResult_' + result.id}
                            id={this.props.widget.id + '__searchResult_' + result.id}
                          >
                            <PlaceCard
                              id={this.props.widget.id + '__placeCard_searchResults_' + result.id}
                              data={{
                                location: result,
                              }}
                              selectedLocation={selectedLocation}
                              onSelectLocation={this.handleSelectLocation}
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography
                          component="p"
                          variant="small"
                          id={this.props.widget.id + '__text_noSearchResults'}
                        >
                          No search results found.
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Typography
                    aria-live="polite"
                    aria-atomic="true"
                    className="sr-only"
                    id={this.props.widget.id + '__mapAnnouncement'}
                  >
                    {selectedLocation?.mapIframeEmbedUrl
                      ? `Map updated to show ${selectedLocation.title}`
                      : ''}
                  </Typography>
                  <Box sx={{ mt: 1 }} id={this.props.widget.id + '__box_sharedMap'}>
                    {selectedLocation?.mapIframeEmbedUrl ? (
                      <PlaceMap
                        id={this.props.widget.id + '__sharedMap'}
                        mapId={selectedLocation.mapId ?? ''}
                        mapIframeEmbedUrl={selectedLocation.mapIframeEmbedUrl}
                        title={`Map of ${selectedLocation.title}`}
                      />
                    ) : (
                      <iframe
                        id={this.props.widget.id + '__campusOverviewMap'}
                        title="Old Dominion University Campus Map"
                        width="100%"
                        height="250"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={
                          CAMPUS_OVERVIEW_EMBED_URL + '&key=' + this.GOOGLE_MAPS_KEY + '&zoom=16'
                        }
                      />
                    )}
                  </Box>
                </div>

                <div
                  className="scrollablePlaces"
                  tabIndex={0}
                  role="region"
                  aria-label="Places categories"
                >
                  <List id={this.props.widget.id + '__list'} className="categories">
                    {this.state.data.map((category) => (
                      <ListItem
                        id={this.props.widget.id + '__listItem_' + category.id}
                        disableGutters
                        key={this.props.widget.id + '__listItem_' + category.id}
                      >
                        <PlaceCategories
                          id={this.props.widget.id + '__placeCategories_' + category.id}
                          data={{ category: category }}
                          selectedLocation={selectedLocation}
                          onSelectLocation={this.handleSelectLocation}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              </>
            )}
          </div>
        </Widget>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  expandedCategories: state.expandedCategories,
});

const mapDispatchToProps = {
  toggleAllCategories,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlacesAndHours);
